import React, { useRef } from 'react';
import { View, TextInput, Text } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import { useNavigation, useRoute } from '@react-navigation/native';

import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { ncFilesSearch } from '@reducers/search';
import { LEVELS } from '@helpers';
import Loading from '@components/loading';
import Item from '@components/search/ncFiles/components/item';
import Small from '@components/search/ncFiles/components/small';
import DataGrid from '@components/common/DataGrid';
import { getNCFileDataGridColumns } from '@components/search/ncFiles/columns';
import { SearchInputWithModeSelector } from '@components/search/components/SearchInputWithModeSelector';
import NcFileStatusFilters from '@components/search/components/NcFileStatusFilters';
import Selectors from '@styles/selectors';
import { NCFileSearchItem } from '@interfaces/search';
import { OperatorPanelInformationSearchNCFilesScreenProps } from './types';
import { useOPDisplayMode } from './hooks/useOPDisplayMode';
import { useNCFilesSearch } from './hooks/useNCFilesSearch';
import { useMachineFilter } from '@components/operatorPanel/hooks/useMachineFilter';
import { OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';

const OperatorPanelInformationSearchNCFilesScreen = ({
  C,
  ncFilesSearch,
}: OperatorPanelInformationSearchNCFilesScreenProps) => {
  const searchRef = useRef<TextInput>(null);
  const navigation = useNavigation<any>();
  const route = useRoute<any>();

  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];
  const machineId = route.params?.id;

  const {
    displayMode,
    setDisplayMode,
    isSmallMode,
    isLineMode,
    isTableMode,
    effectiveWidth,
    columns,
    itemSize,
  } = useOPDisplayMode();

  const {
    selectedFilter,
    setSelectedFilter,
    ncFileMachineFilter,
    filterOptions,
    isFilterReady,
    isEmptyFilter,
  } = useMachineFilter();

  const {
    searchText,
    setSearchText,
    searchResults,
    totalCount,
    loading,
    facets,
    facetsLoaded,
    selectedStatusFilter,
    handleSearch,
    handleLoadMore,
    handleFilterChange,
    handleCancelSearch,
    gridSearch,
  } = useNCFilesSearch({ ncFilesSearch, machineFilter: ncFileMachineFilter, isFilterReady, isEmptyFilter });

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/System [mobile]`, false);

  const goToDetails = (item: NCFileSearchItem) => {
    const navigationParams: any = {
      ncFileId: item.ncFileId,
      ncFileName: item.ncFileName,
    };
    if (selectedJobId) {
      navigationParams[scheduledProductionOperationIdParam] = selectedJobId;
    }
    if (machineId) {
      navigationParams.id = machineId;
    }
    navigation.navigate(OPERATOR_PANEL_INFORMATION_SCREENS.Information, {
      screen: OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles,
      params: navigationParams,
    });
  };

  // Get DataGrid columns
  const dataGridColumns = getNCFileDataGridColumns({
    scale: scaleOP,
    goToDetails,
    attachMode: false,
  });

  const renderItem = ({ item, index }: { item: NCFileSearchItem; index: number }) => {
    if (!item) return null;

    const columnIndex = index % columns;
    const marginRight = !isLineMode && columns > 1 && columnIndex < columns - 1 ? scaleOP(isSmallMode ? 8 : 18) : 0;
    const marginBottom = !isLineMode ? scaleOP(isSmallMode ? 8 : 18) : 0;

    if (isLineMode) {
      return (
        <Small
          item={item}
          goToDetails={goToDetails}
          attachMode={false}
          effectiveWidth={effectiveWidth}
        />
      );
    }

    return (
      <View style={{ marginRight, marginBottom }}>
        <Item
          item={item}
          index={index}
          itemSize={itemSize}
          goToDetails={goToDetails}
          isSmallMode={isSmallMode}
          attachMode={false}
        />
      </View>
    );
  };

  const renderHeader = () => (
    <View style={{ marginBottom: scaleOP(16) }}>
      <SearchInputWithModeSelector
        C={C}
        showModeSelector={true}
        displayMode={displayMode}
        setSelectedDisplayMode={setDisplayMode}
        level={LEVELS.NCFiles}
        searchRef={searchRef}
        searchValue={searchText}
        onSearch={setSearchText}
        submit={handleSearch}
        cancelAnimation={handleCancelSearch}
        bg="Background/Light/Search & View Selector"
        scale={scaleOP}
        font={fontOP}
      />
      <NcFileStatusFilters
        facets={facets}
        facetsLoaded={facetsLoaded}
        selectedFilter={selectedStatusFilter}
        onFilterChange={handleFilterChange}
        C={C}
        scale={scaleOP}
        font={fontOP}
        style={{ marginTop: scaleOP(12), paddingHorizontal: 0 }}
        containerMarginBottom={scaleOP(12)}
        availableWidth={effectiveWidth}
      />
      <Text style={[fontOP['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
        {totalCount.toLocaleString()} {i18n.t('results')}
      </Text>
    </View>
  );

  const filterTabs = (
    <View style={{ flexDirection: 'row', gap: scaleOP(8) }}>
      {filterOptions.map((option) => (
        <Selectors
          key={option.id}
          variant="L"
          text={option.name}
          selected={selectedFilter === option.id}
          onPress={() => setSelectedFilter(option.id)}
          scale={scaleOP}
          font={fontOP}
        />
      ))}
    </View>
  );

  return (
    <View testID={TEST_IDS.operatorPanel.information.searchNCFiles.root} style={{ backgroundColor: bg, flex: 1 }}>
      <TopPanel title="NC Files" withBottomBorder submit={() => {}} centerContent={filterTabs} />
      <View style={{ paddingHorizontal: scaleOP(18), flex: 1, paddingTop: scaleOP(16) }}>
        {renderHeader()}
        {loading && searchResults.length === 0 ? (
          <Loading background={bg} />
        ) : isTableMode ? (
          <View style={{ flex: 1, width: effectiveWidth }}>
            <DataGrid
              columns={dataGridColumns}
              data={searchResults}
              primaryKey="ncFileName"
              onRowClick={goToDetails}
              idName="ncFileId"
              tableName={TABLE_NAMES.operatorPanelSearchNCFiles}
              loaded={!loading}
              isError={false}
              density={displayMode}
              scale={scaleOP}
              search={gridSearch}
              onRowsScrollEnd={handleLoadMore}
            />
          </View>
        ) : (
          <FlashList
            data={searchResults}
            renderItem={renderItem}
            keyExtractor={(item) => item?.ncFileId?.toString()}
            numColumns={isLineMode ? 1 : columns}
            key={`${columns}-${displayMode}`}
            contentContainerStyle={{ paddingBottom: scaleOP(24) }}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>
    </View>
  );
};

export default connect(null, { ncFilesSearch })(OperatorPanelInformationSearchNCFilesScreen);
